import { httpClient, type BlobDownload } from "../http-client";
import type {
  EventAttendanceSummaryDto,
  EventImagesUploadEventImagesResponses,
  EventNotificationMuteStateDto,
  EventsGetParticipantsData,
  RegisterForEventResponseDto,
} from "../../api/generated/types.gen";
import { buildQueryString } from "./query-builder";
import {
  ApiResponse,
  PaginatedApiResponse,
} from "../../types/responses/api-response";
import { EventRole } from "../../types/enums/event-roles.enum";
import { eventBudgetItemToCreateDto } from "../../utils/budget-mappers";
import { buildEventPayload } from "../mappers/event-mapper";
import type {
  AgendaItemWrite,
  ProposedAgendaItem,
} from "../../utils/events/template-program";
import {
  Event,
  EventParticipant,
  EventType,
  EventFormat,
  EventStatus,
  ParticipantStatus,
  ParticipantRole,
  ParticipationMode,
  EventLocation,
  EventAgenda,
  EventBudget,
  EventBudgetItem,
  EventDocument,
  EventStats,
  EventVisibility,
} from "../../types/events-unified";

// ===== EVENTS API =====

// Контракт event-notification-campaign (BE CreateEventNotificationDto).
// Значения совпадают со строковыми enum на BE.
export type EventNotificationChannel = "email" | "sms" | "push" | "in_app";
export type EventNotificationType =
  | "event_update"
  | "schedule_change"
  | "participant_action"
  | "team_announcement"
  | "reminder"
  | "urgent";
export type EventNotificationPriority = "low" | "normal" | "high" | "urgent";

// Типы для создания и обновления событий
export interface CreateEventRequest {
  title: string;
  description?: string;
  startDate: string | Date;
  endDate: string | Date;
  type: string;
  status?: string; // по умолчанию 'draft'
  visibility?: string; // по умолчанию 'private'

  /**
   * AD-389. Формат УЧАСТИЯ, словарь серверный (`EventFormat`). Голая строка
   * здесь означала, что мастер мог отправить любое слово, и узнал бы об этом
   * только отказом сервера: у колонки домена `@IsEnum`, а не свободный текст.
   * У `UpdateEventRequest` ниже тип был перечислением с самого начала.
   */
  format?: EventFormat;
  timezone?: string;
  location?: EventLocation;
  organizerId?: string;
  maxParticipants?: number;
  registrationDeadline?: string | Date;
  tags?: string[];
  category?: string;
  // Н-6 (приёмка волны 11–12.09.2026). Здесь стояли `isPublic`,
  // `requiresApproval`, `allowGuestRegistration`. Ни одного из трёх имён нет ни
  // в `CreateEventDto`/`UpdateEventDto` бэкенда, ни в таблице `events`, ни вообще
  // в его исходниках, а белый список `CREATE_EVENT_DTO_KEYS` в
  // `mappers/event-mapper.ts` их не пропускал — то есть тип обещал три
  // переключателя, которые молча никуда не уходили. Публичность выражается
  // `visibility` (AD-131/AD-180); одобрение заявок и гостевая регистрация в
  // системе пока не реализованы, и объявлять их в контракте нельзя.
  coverImage?: string;
  images?: string[];
  /**
   * AD-500. Плоский массив канонических пунктов повестки. Сервер принимает его
   * только здесь, на создании: `POST /events` заводит пункты в
   * `event_agenda_items` в той же транзакции. Раньше тип обещал jsonb-объект
   * `EventAgenda` — такой формы в контракте больше нет.
   */
  agenda?: AgendaItemWrite[];
  budget?: EventBudget;
  team?: Array<{ name: string; role: string }>;
  invitations?: {
    template: string | null;
    channels: string[];
    customizations: Record<string, unknown>;
  };
  style?: { theme: string };
  contingency?: { risks: string };
  targetAudience?: string[];
}

export interface UpdateEventRequest {
  title?: string;
  description?: string;
  type?: EventType;
  format?: EventFormat;
  startDate?: string | Date;
  endDate?: string | Date;
  timezone?: string;
  location?: EventLocation;
  organizerId?: string;
  maxParticipants?: number;
  currentParticipants?: number;
  registrationDeadline?: string | Date;
  tags?: string[];
  category?: string;
  // Н-6 (приёмка волны 11–12.09.2026). Здесь стояли `isPublic`,
  // `requiresApproval`, `allowGuestRegistration`. Ни одного из трёх имён нет ни
  // в `CreateEventDto`/`UpdateEventDto` бэкенда, ни в таблице `events`, ни вообще
  // в его исходниках, а белый список `CREATE_EVENT_DTO_KEYS` в
  // `mappers/event-mapper.ts` их не пропускал — то есть тип обещал три
  // переключателя, которые молча никуда не уходили. Публичность выражается
  // `visibility` (AD-131/AD-180); одобрение заявок и гостевая регистрация в
  // системе пока не реализованы, и объявлять их в контракте нельзя.
  coverImage?: string;
  images?: string[];
  // AD-500. `agenda` здесь больше нет — и это гарантия типом, а не стилем.
  // `UpdateEventDto` бэкенда поле снял, а глобальный пайп работает с
  // `forbidNonWhitelisted`: ключ в теле PATCH роняет весь запрос в 400, то
  // есть правка события целиком. Программа правится дверями
  // `/events/:id/agenda` (`createAgendaItem`/`updateAgendaItem`/
  // `deleteAgendaItem` ниже).
  budget?: EventBudget;
  price?: number;
  currency?: string;
  organizerName?: string;
  status?: string;
}

// Типы для участников
export interface CreateParticipantRequest {
  eventId: string;
  userId: string;
  email: string;
  name: string;
  fullName: string;
  role: ParticipantRole;
  status: ParticipantStatus;
  dietary?: string[];
  specialRequests?: string;
  emergencyContact?: string;
  source: string;
  metadata?: Record<string, any>;
}

export interface UpdateParticipantRequest {
  email?: string;
  name?: string;
  fullName?: string;
  role?: ParticipantRole;
  status?: ParticipantStatus;
  dietary?: string[];
  specialRequests?: string;
  emergencyContact?: string;
  metadata?: Record<string, any>;
}

/**
 * Собственная запись участия в ответе `GET /events/:id/registration-status` —
 * зеркало BE `ParticipantDetail` (`query-results.interface.ts`) в том виде, в
 * каком оно приходит по сети: даты — СТРОКИ.
 *
 * AD-302. Прежде ответ был объявлен как `EventParticipant`, где
 * `registeredAt: Date`, и карточка регистрации вызывала на этой строке
 * `toISOString()`. `TypeError` уходил в `catch`, и у каждого записавшегося после
 * перезагрузки страницы вместо статуса и кнопки «Отменить регистрацию» была
 * плашка «не удалось загрузить статус».
 */
export interface OwnRegistrationParticipant {
  id: string;
  eventId: string;
  userId: string;
  status: ParticipantStatus;
  role: ParticipantRole;
  registeredAt: string;
  checkedInAt: string | null;
  notes: string | null;
  /**
   * AD-389 шаг 6. Способ участия, заявленный САМИМ вошедшим.
   *
   * Единственный ответ, которым человек видит СВОЁ намерение: маршрут
   * самоскоуплен по токену, чужое через него не прочитать. Без этого поля
   * выбор был бы «отправил и забыл» — экран показал бы кнопку смены, не зная,
   * что менять, а человек не смог бы убедиться, что ответ записан.
   *
   * `null` значит «не заявлен», а НЕ «очно» (см. `ParticipationMode`).
   */
  participationMode: ParticipationMode | null;
  /**
   * AD-531. Участие прекратил ОРГАНИЗАТОР (заявка отклонена или участие
   * отменено им), а не сам человек ответом «не пойду». Статус этого не
   * различает: `cancelled` бывает у обоих, а исходы противоположные — первому
   * запись отвечает `REGISTRATION_REJECTED`, второго возвращает. Необязательно:
   * бэкенд, на который поле ещё не выкачено, его не пришлёт (TD-31), и тогда
   * причину назовёт отказ сервера после нажатия.
   */
  endedByOrganizer?: boolean;
  user: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}

/**
 * Ответ `GET /events/:id/registration-status` (BE `getRegistrationStatus`,
 * `events.service.ts`). `participant` — `null`, когда записи нет; запись
 * приходит и при `registered: false` (приглашение, отказ).
 */
export interface OwnRegistrationStatus {
  registered: boolean;
  participant: OwnRegistrationParticipant | null;
  /**
   * AD-412. МЕСТО В ОЧЕРЕДИ — число, а не фраза «вы в листе ожидания».
   *
   * Приходит только стоящему в очереди; во всех прочих состояниях сервер
   * отвечает `null` (у «заявки на рассмотрении» очереди нет вовсе — там решает
   * организатор, и число рядом читалось бы как обещание). `#1` — того повысят
   * следующим: величина считается тем же предикатом и ключом, которыми сервер
   * выбирает, кого поднять из очереди.
   *
   * СОСТОЯНИЙ ТРИ, И ТРЕТЬЕ — `undefined`: «сервер об этом не говорил» (TD-31,
   * окно между выкатками бэкенда и фронта). `null` — другое: сервер ответил
   * «места в очереди нет». Смешать их значит в окно выкатки показать человеку
   * «вы не в очереди» ровно тогда, когда он в ней стоит.
   *
   * Поле лежит РЯДОМ с записью участия, а не внутри неё, потому что оно не
   * свойство записи: та же строка завтра станет третьей, не изменившись ни
   * одним полем.
   */
  waitlistPosition?: number | null;
}

/**
 * AD-389 шаг 4. Причина, по которой вход на онлайн-часть не выдан — машинный
 * словарь BE (`OnlineAccessDenial`, `online-access.constants.ts`). Значения
 * повторены здесь строками, потому что общего рантайм-словаря у репозиториев
 * нет; расхождение ловится тестом соответствия с генератом контракта.
 *
 * ПОЧЕМУ ПРИЧИН НЕСКОЛЬКО, А НЕ ОДНО «НЕЛЬЗЯ». Причина — это текст, который
 * человек увидит вместо ссылки, и следующий шаг у него разный: записанному
 * подтвердить участие (единственное, что зависит от него самого), стоящему в
 * очереди — дождаться места, приглашённому — ответить на приглашение,
 * получившему право — дождаться, пока организатор назначит встречу.
 */
export type OnlineAccessDenial =
  | "not_registered"
  | "confirmation_required"
  // На ПЛАТНОМ событии подтверждает ОРГАНИЗАТОР, получив деньги (решение
  // владельца 20.09.2026): кнопка «подтвердить участие» здесь не показывается
  // вовсе — сервер ей всё равно откажет (`PAID_PARTICIPATION_CONFIRMED_BY_ORGANIZER`).
  | "payment_pending"
  | "awaiting_approval"
  | "waitlisted"
  | "invitation_pending"
  | "participation_ended"
  // AD-531. Заявку отклонил организатор — своя причина, как у стойки отметки:
  // «участие завершено» звучало бы как итог, к которому человек пришёл сам.
  | "application_rejected"
  | "link_not_published"
  // AD-396. У события нет онлайн-части вовсе: оно проводится очно. Причина
  // отдельная от `link_not_published` намеренно — ту снимает организатор,
  // назначив ссылку, а эту не снимает ничто, кроме смены формата события.
  | "format_has_no_online_part";

/**
 * Ответ `GET /events/:id/online-access` (BE `EventOnlineAccess`).
 *
 * Отказ здесь — штатный ответ 200 с названной причиной, а не ошибка: самый
 * частый исход — «только что записался и ещё не подтвердил участие», и экран
 * обязан показать следующий шаг, а не «что-то пошло не так».
 *
 * Имена `link`/`stream` НЕ совпадают с колонками события (`virtualLink`,
 * `liveStreamUrl`) намеренно: те имена запрещены в любом ответе целиком
 * (`@Exclude` на сущности + замок секретов), и запрет держится сам, без
 * перечня охраняемых маршрутов. Значение едет под именем контракта уже после
 * того, как право на него решено.
 */
export interface EventOnlineAccess {
  available: boolean;
  denial: OnlineAccessDenial | null;
  link: string | null;
  stream: string | null;
  format: EventFormat;
}

/**
 * AD-409. Ответ управляющих маршрутов ссылки: `GET` и
 * `DELETE /events/:id/online-access/link`, `DELETE .../online-access/stream`
 * (BE `ManagedOnlineLink`).
 *
 * ПОЧЕМУ ОТДЕЛЬНАЯ ФОРМА, А НЕ `EventOnlineAccess`. У вида входа есть поле
 * `available` — ответ на вопрос «пустят ли МЕНЯ на встречу», и с гейтом формата
 * у очного события оно `false` ВСЕГДА, включая организатора. Управляющая
 * сторона спрашивает другое: «что сейчас назначено СОБЫТИЮ». Читай мастер
 * правки `available` как «ссылка назначена» — у события, переведённого в
 * `offline`, он показал бы «ссылки нет» при живой ссылке в базе. Из этого
 * расхождения AD-409 и выросла.
 */
export interface ManagedOnlineLink {
  /** Назначенная ссылка на встречу; `null` — не назначена. */
  link: string | null;
  /** Назначенный адрес трансляции; `null` — не назначен. */
  stream: string | null;
  /**
   * Формат события — ПОДПИСЬ для экрана («онлайн-части нет, а ссылка осталась»),
   * а не условие выдачи: на этих маршрутах формат не решает ничего.
   */
  format: EventFormat;
}

/**
 * Ответ `POST /events/:id/online-access/join` (BE `EventOnlineJoin`) — переход
 * по ссылке на онлайн-встречу.
 *
 * Решение владельца 20.09.2026: «чек-ин на онлайн-мероприятии — по факту
 * перехода по ссылке». Поэтому у перехода есть серверное действие, а не только
 * `href`: присутствие отмечает сервер, экран лишь сообщает о переходе.
 *
 * `attendanceRecorded: false` — НЕ отказ: так отвечает повторный переход (уже
 * отмечен) и переход до начала или после конца встречи. Вход при этом выдаётся
 * по своему правилу, независимо от отметки.
 */
export type OnlineAttendanceOutcome =
  /** Отметка поставлена ЭТИМ переходом. */
  | "recorded"
  /** Отметка уже стояла: повтор перехода или второе устройство. */
  | "already_recorded"
  /** Встреча ещё не началась — присутствие отметится в её время. */
  | "not_started"
  /** Встреча закончилась: отметить присутствие уже нельзя. */
  | "ended"
  /** Отмечать некого: вход не выдан либо человек не участник события. */
  | "not_eligible";

export interface EventOnlineJoin extends EventOnlineAccess {
  attendanceRecorded: boolean;
  /**
   * AD-410. ПОЧЕМУ отметки нет — машинным кодом сервера.
   *
   * Одного `attendanceRecorded: false` экрану мало: так отвечают четыре разных
   * случая, и человеку в каждом надо сказать своё — подождать начала, уже
   * ничего не делать, не отмечаться повторно. Раньше экран молчал, и молчал не
   * по недосмотру: различить случаи по ответу было НЕЧЕМ.
   *
   * ПОЧЕМУ КОД, А НЕ ГОТОВАЯ СТРОКА. Решение владельца 20.09.2026: показывать
   * на языке локали пользователя. Текст, собранный на сервере, приехал бы на
   * одном языке всем, поэтому слова живут в словарях этого репозитория, а с
   * сервера приезжает только код.
   *
   * Значение вне этого словаря (сервер расширили) экран показывает МОЛЧАНИЕМ, а
   * не выдуманным объяснением — тем же правилом, что `OnlineAccessDenial`.
   */
  attendanceOutcome: OnlineAttendanceOutcome;
  checkedInAt: string | null;
}

/**
 * Ответ `POST /events/:id/participation/confirm` — человек подтверждает СВОЁ
 * участие. `changed: false` означает «уже было подтверждено» (двойной клик,
 * повтор после разрыва связи), а не отказ.
 */
export interface ConfirmParticipationResult {
  status: ParticipantStatus;
  changed: boolean;
}

/**
 * Ответ `POST /events/:id/register` — BE `RegisterResult`. Это не запись
 * участника: идентификатор лежит в `participantId`, даты регистрации в ответе нет.
 *
 * AD-417. ПОЛЯ БЕРУТСЯ ИЗ ГЕНЕРАТА, А НЕ ОБЪЯВЛЯЮТСЯ ЗАНОВО. Прежде это был
 * самостоятельный интерфейс, и сверять его было не с чем: у маршрута в
 * контракте вообще не было схемы ответа (`@ApiResponse` без `type`), поэтому
 * переименование поля на сервере компилятор бы не заметил — ровно тот класс
 * дефектов, что замерен в AD-415 на кабинете участника. Схема заведена той же
 * правкой, и теперь имена и типы полей приходят от неё.
 *
 * `status` сужен до `ParticipantStatus` осознанно: генерат отдаёт объединение
 * строк, а экраны сравнивают состояние с членами перечисления. Множество
 * значений то же самое — все девять, — так что сужение ничего не отрезает,
 * а разъезд перечисления с контрактом поймает этот же тип.
 */
export type RegisterForEventResult = Omit<
  RegisterForEventResponseDto,
  "status"
> & {
  status: ParticipantStatus;
};

/**
 * AD-389 шаг 6. Ответ `PATCH /events/:id/participation-mode` — BE
 * `ParticipationModeResult`. Возвращается ровно то, что изменилось: статуса
 * участия здесь нет намеренно, эта операция его не трогает, и экран не обязан
 * перечитывать участника ради одной колонки.
 */
export interface ParticipationModeResult {
  success: boolean;
  message: string;
  participantId: string;
  participationMode: ParticipationMode;
}

/** Ответ `DELETE /events/:id/unregister` — BE `UnregisterResult`. */
export interface UnregisterFromEventResult {
  success: boolean;
  message: string;
  participantId: string;
}

// Типы для фильтрации и поиска (принимаем Date/массивы для совместимости с хуками)
export interface EventFilters {
  page?: number;
  limit?: number;
  search?: string;
  type?: EventType | EventType[];
  /**
   * AD-389 (шаг 2). Формат УЧАСТИЯ — собственная колонка мероприятия
   * (`events.format`), и с появлением её в контракте отбора ключ уезжает на
   * сервер на обоих маршрутах — и в списке, и в выгрузке. До этого шага он давал
   * 400 на весь запрос, поэтому клиент его сознательно терял, а фильтр формата на
   * календаре ничего не менял. Словарь совпадает с серверным `EventFormat`
   * (`online | offline | hybrid`), иначе значение отклоняется отказом.
   */
  format?: EventFormat | EventFormat[];
  status?: EventStatus | string;
  /** Одна категория из `Event.categories` (AD-388). */
  category?: string;
  organizerId?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  isPublic?: boolean;
  tags?: string[];
  /** AD-388. Подстрока в месте проведения. */
  location?: string;
  /** AD-388. Границы цены участия; событие без цены считается бесплатным. */
  minPrice?: number;
  maxPrice?: number;
  /** AD-388. Порядок выдачи; набор значений задан сервером. */
  sortBy?: EventsSortField;
  sortOrder?: EventsSortOrder;
}

/**
 * AD-388. Зеркало серверного перечисления (`EventsSortField`): имя колонки
 * подставляется в `ORDER BY` и параметром быть не может, поэтому сервер
 * принимает только эти значения и на любое другое отвечает 400.
 */
export type EventsSortField = "startDate" | "title" | "price" | "createdAt";
export type EventsSortOrder = "ASC" | "DESC";

export interface ParticipantFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: ParticipantStatus;
  role?: ParticipantRole;
  registeredAfter?: string;
  registeredBefore?: string;
  /** Вопрос 28, ч. 2. Фильтр «Ответ на приглашение». */
  invitationResponse?: InvitationResponseState;
}

/**
 * Вопрос 28, ч. 2. Состояние ответа на приглашение — из контракта маршрута
 * (`GET /events/:eventId/participants`, query `invitationResponse`), а не
 * переписанным списком: новое значение на сервере приедет сюда генерацией.
 */
export type InvitationResponseState = NonNullable<
  NonNullable<EventsGetParticipantsData["query"]>["invitationResponse"]
>;

/**
 * POST /events/:eventId/invite (зеркало BE BulkInviteDto,
 * ADR-INVITES-post-creation-invite-delivery): userIds — UUID, emails —
 * валидные адреса (≤500 каждый), message ≤1000 символов.
 */
export interface InviteParticipantsRequest {
  userIds?: string[];
  emails?: string[];
  message?: string;
  /**
   * Event-роль (authz), выдаваемая приглашённому ПОСЛЕ accept
   * (ADR-STAFF-invite-with-role). Зеркало BE BulkInviteDto.role.
   */
  role?: EventRole;
  /** Сегмент приглашения (VIP/press/sponsor/…). Зеркало BE BulkInviteDto.segment. */
  segment?: string;
}

/**
 * Элемент списка участников события — ТОЧНОЕ зеркало BE ParticipantSummary
 * (GET /events/:id/participants). Внимание: имя/почта вложены в `user`, НЕ
 * плоские (в отличие от legacy-типа EventParticipant — источник дрейфа
 * контракта, из-за которого списки StaffTab/GuestsTab показывали пустые поля).
 * eventRole/segment — дисплей-маркеры StaffTab (ADR-STAFF-invite-with-role).
 */
export interface EventParticipantSummary {
  id: string;
  userId: string;
  eventId: string;
  status: ParticipantStatus;
  role: ParticipantRole;
  registeredAt: string;
  eventRole: string | null;
  segment: string | null;
  /** Время чек-ина (null — не отмечался). */
  checkedInAt: string | null;
  /**
   * AD-463. ПРИШЁЛ ЛИ — ответ СЕРВЕРА по составному критерию (флаг
   * `hasAttended` ИЛИ статус `attended`), а не вывод экрана.
   *
   * Читать вместо `status === 'attended'`: приход, отмеченный на стойке,
   * статуса не меняет, и такой гость выглядел бы неотмеченным. Время
   * `checkedInAt` тоже не ответ — приход, проставленный сменой статуса,
   * времени за собой не оставляет.
   *
   * Это НЕ «в зале сейчас»: присутствие в моменте — факт журнала
   * `event_attendance`, и уход его гасит. `attended` значит «был на
   * событии» и после ухода остаётся истинным (AD-461).
   */
  attended: boolean;
  /**
   * AD-389 шаг 6. Заявленный участником способ участия — ради этого числа поле
   * и заведено: организатор гибрида планирует зал и связь по нему. `null` —
   * «не заявлен», и в такой столбец нельзя подставлять «очно».
   */
  participationMode: ParticipationMode | null;
  /** Код регистрации для ручного чек-ина из списка. */
  registrationCode: string | null;
  /**
   * Вопрос 28, часть 2. Ответ гостя на приглашение — состояние считает СЕРВЕР
   * (`invitation-response.constants.ts`), тем же критерием, что и фильтр
   * `invitationResponse`. `awaiting` — приглашён и молчит; `null` — ответа
   * ждать не от кого (записался сам, завёл организатор, приглашение отменено).
   * Ответ — факт, а не текущее участие: его показывает `status`.
   */
  invitationResponse: InvitationResponseState | null;
  /** Когда гость ответил (ISO); `null` — ответа нет. */
  invitationRespondedAt: string | null;
  user: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    avatar: string | null;
  };
}

/**
 * AD-511. Ответ POST /events/:eventId/checkin/by-code — зеркало BE
 * `CheckInByCodeResult`. Участник — та же проекция, что строка списка: имя и
 * почта во вложенном `user`, служебного (заметки организатора, причин отказа)
 * нет.
 *
 * До AD-511 сервер отдавал сырую строку участника без учётки, а тип здесь
 * обещал плоский `EventParticipant` с `fullName`/`email` — полей, которых на
 * проводе не было ни в одной версии. Плашка сканера печатала «()».
 */
export interface CheckInByCodeResult {
  participant: EventParticipantSummary;
  /** Отметка уже была: повтор — no-op, второй записи журнала сервер не делает. */
  alreadyCheckedIn: boolean;
}

/**
 * Ответ GET /events/:id/participants на ПРОВОДЕ: `data` — массив участников,
 * счётчики — плоско в `meta` (ADR-002 п.3, тип `PaginatedApiResponse`).
 *
 * Здесь раньше стоял конверт `{ items, total, … }` — зеркало возврата
 * КОНТРОЛЛЕРА `toPaginatedContract`. Но контроллер не последний в цепочке: его
 * результат разворачивает глобальный `ResponseInterceptor`, и на провод уходит
 * уже `{ success, data: items, meta }`. Тип описывал промежуточную форму, из-за
 * чего `data.items` компилировался и молча давал `undefined` — находка Н-9,
 * пустой список участников на ВСЕХ экранах организатора.
 */
export type ParticipantsPageResponse =
  PaginatedApiResponse<EventParticipantSummary>;

/**
 * Запись листа ожидания — участник плюс НОМЕР В ОЧЕРЕДИ, посчитанный сервером.
 *
 * Номер приходит с бэкенда не для удобства: очередь пагинируется, и позиция
 * внутри загруженной страницы номером в очереди не является. Считать его на
 * клиенте («индекс + 1») — ровно тот дефект, из-за которого экран показывал
 * «#1» над случайным участником.
 */
export interface WaitlistEntry extends EventParticipantSummary {
  /** 1-based позиция в очереди, сквозная по страницам. */
  position: number;
}

/** Ответ GET /events/:id/waitlist — тот же провод: массив в `data`, числа в `meta`. */
export type WaitlistPageResponse = PaginatedApiResponse<WaitlistEntry>;

/**
 * Сводка посещаемости события — тип из генерата (`EventAttendanceSummaryDto`)
 * (`GET /events/attendance/event/:eventId/summary`, AD-464).
 *
 * КРУГ, который считает сервер: гости, занимающие место (статусы
 * `registered`/`confirmed`/`attended`), БЕЗ строки организатора. Тот же круг,
 * которым считает карточка события (AD-438) и счётчик мест (AD-437). Список
 * `getEventParticipants` круг НЕ сужает — он отдаёт все строки участия,
 * поэтому строк в таблице законно больше, чем `totalParticipants`.
 */
export type AttendanceSummaryResponse = EventAttendanceSummaryDto;

/**
 * Строка журнала отметок (`event_attendance`). Отметок у одного человека может
 * быть несколько: приход, перерыв, уход — они различаются полем `type`.
 *
 * `status` — вид отметки (`present`/`absent`/`late`/`excused`/`partial`),
 * `type` — её род (`check_in`/`check_out`/`break_in`/`break_out`/`manual`).
 */
export interface AttendanceJournalRow {
  id: string;
  eventId: string;
  userId: string;
  type: string;
  status: string;
  timestamp: string;
}

/**
 * AD-460. ОТВЕТ МАРШРУТА `GET /events/:id/qr/:userId` — в той форме, в которой
 * он приходит с провода.
 *
 * Прежде фронт объявлял здесь две трети от трети: `{ qrCode, participantId }`.
 * Экран организатора показывал картинку в диалоге, и большего ему не требовалось;
 * но участнику, который придёт с этим кодом к двери, недостающие поля несущие.
 *
 * `expiresAt` — САМОЕ ВАЖНОЕ ИЗ ПРОПУЩЕННЫХ. В QR закодирован подписанный
 * кратко-живущий токен (ADR-CHECKIN-TOKEN), а не открытый идентификатор, и живёт
 * он `QR_CODE_TTL_HOURS` часов — по умолчанию 24. Значит скриншот, сделанный за
 * неделю до события, у двери не сработает: сканер проверяет `exp` fail-closed.
 * Экран, который срока не показывает, обещает человеку то, чего у него не будет.
 *
 * `registrationCode` — запасной путь: сотрудник вводит код руками, когда камера
 * не берёт экран. Сервер отдаёт `participant.registrationCode`, а при его
 * отсутствии — идентификатор записи участия.
 */
export interface ParticipantQRResponse {
  /** `data:image/png;base64,…` — картинка, а не содержимое кода */
  qrCode: string;
  eventId: string;
  userId: string;
  participantId: string;
  registrationCode: string;
  /** ISO-строка на проводе: JSON чисел-дат не знает */
  expiresAt: string;
}

/** Per-item статус приглашения по userId */
export interface InviteDetail {
  userId: string;
  status: "invited" | "already_invited";
}

/** Контракт ответа invite-эндпоинта (зеркало BE InviteParticipantsResult) */
export interface InviteParticipantsResult {
  invited: number;
  alreadyInvited: number;
  errors: number;
  details: InviteDetail[];
  emails: {
    registered: number;
    invitedByEmail: number;
    /**
     * AD-251. Незарегистрированных адресов, которым письмо НЕ отправлено:
     * у события нет рабочей публичной ссылки `/join/:token`, а вести их
     * больше некуда (приглашение незарегистрированного не создаёт записи
     * участия, и `GET /events/:id` ответит им 404 — `AD-118`).
     *
     * Отдельное поле, а не часть `invitedByEmail`: тот означает «письмо
     * ушло». Сложить их — значит отчитаться организатору об отправке,
     * которой не было.
     *
     * Поле пришло с BE позже самого эндпоинта, поэтому ответ развёрнутого
     * ранее сервера его не содержит; читать только через сравнение
     * (`> 0`), которое на `undefined` честно даёт «нечего показывать».
     */
    skippedUnregistered: number;
    /** Причина пропуска — присутствует ровно когда `skippedUnregistered > 0`. */
    skippedReason?: "self_registration_disabled";
  };
}

/** Контракт ответа RSVP-эндпоинта (зеркало BE RsvpResult, ADR-RSVP) */
export interface RsvpResult {
  success: boolean;
  message: string;
  participantId: string;
  response: "going" | "not_going" | "maybe";
  status: string;
}

// Типы для статистики
export interface EventAnalytics {
  eventsByMonth: Array<{ month: string; count: number }>;
  participantsByEvent: Array<{
    eventId: string;
    eventTitle: string;
    participants: number;
  }>;
  popularCategories: Array<{ category: string; count: number }>;
  attendanceRate: number;
  revenueByEvent?: Array<{
    eventId: string;
    eventTitle: string;
    revenue: number;
  }>;
  engagementMetrics: {
    averageEngagement: number;
    totalInteractions: number;
    activeParticipants: number;
  };
}

/**
 * Форматы выгрузки участников — словарь БЭКЕНДА (`ExportParticipantsQueryDto`).
 * Отдельный тип от `EventExportFileFormat` ниже: словари сегодня совпадают, но
 * отвечают за них два разных маршрута и два разных DTO, и общий тип связал бы
 * их изменения друг с другом без основания.
 */
export type ParticipantExportFormat = "csv" | "xlsx";

/**
 * Формат ФАЙЛА выгрузки каталога мероприятий — зеркало серверного
 * `ExportEventsQueryDto.fileFormat`.
 *
 * AD-392. Здесь был `ExportOptions` с тройкой `csv | excel | pdf` и флагами
 * `includeParticipants` / `includeAnalytics`. Сервер не умел ни одного из этих
 * четырёх значений никогда: выгрузка строит `csv` или `xlsx`, а состав файла
 * фиксирован. Пока лишние ключи молча игнорировались, тип обещал человеку
 * выбор, которого нет; с AD-390 они получают отказ, и расхождение стало
 * наблюдаемым. Параллель у выгрузки участников — `ParticipantExportFormat`.
 */
export type EventExportFileFormat = "csv" | "xlsx";

// ===== НОРМАЛИЗАЦИЯ =====

/** Единственное место нормализации поля изображения события.
 *  Backend: `coverImage`. Старые API-ответы могут содержать алиасы `imageUrl`/`image`. */
export const normalizeEventImage = <
  T extends { coverImage?: string; imageUrl?: string; image?: string },
>(
  raw: T,
): T & { coverImage: string | undefined } => ({
  ...raw,
  coverImage: raw.coverImage ?? raw.imageUrl ?? raw.image,
});

/**
 * Поля отбора в строке запроса: каждое должно быть НАЗВАНО.
 *
 * AD-392. Тип не украшение: именно молчаливо пропущенное поле было дефектом —
 * выгрузка собирала четыре ключа из шестнадцати, и `status` с `search` терялись до
 * сети. Теперь новое поле в `EventFilters` не соберётся, пока его не назовут здесь.
 *
 * Листание исключено: у выгрузки его нет по построению (файл отдаётся по всей
 * выборке, а не по её странице), а `isPublic` — имя клиентской стороны, которое
 * переводится в `visibility` (AD-131).
 */
type EventsSelectionQuery = Record<
  keyof Omit<EventFilters, "page" | "limit" | "isPublic">,
  unknown
> & { readonly visibility: EventVisibility | undefined };

/**
 * ОДНА сборка параметров отбора — на список и на выгрузку.
 *
 * AD-390/AD-392. Разошлись они именно потому, что собирались в двух местах:
 * список слал поиск, статус, место, цену и порядок, а выгрузка — даты, категорию и
 * организатора, поэтому человек уносил в работу файл со всеми своими
 * мероприятиями как «текущую выборку». Сервер свою половину уже свёл в один класс
 * (`EventsFilterQueryDto`), и второй список полей здесь был бы третьим ответом на
 * тот же вопрос.
 */
const eventsSelectionQuery = (filters?: EventFilters): EventsSelectionQuery => {
  const asIso = (value: string | Date | undefined): string | undefined =>
    value instanceof Date ? value.toISOString() : value;
  /** У серверных полей отбора значение скалярное: берётся первое. */
  const first = <T>(value: T | T[] | undefined): T | undefined =>
    Array.isArray(value) ? value[0] : value;

  return {
    search: filters?.search,
    type: first(filters?.type),
    format: first(filters?.format),
    status: filters?.status,
    category: filters?.category,
    organizerId: filters?.organizerId,
    startDate: asIso(filters?.startDate),
    endDate: asIso(filters?.endDate),
    // AD-131. Параметра `isPublic` у бэкенда нет и не было: своя колонка
    // называется `visibility` и принимает public/private/internal. Пока сюда
    // уходило `isPublic=true`, глобальный ValidationPipe отвечал 400 «property
    // isPublic should not exist» — три списка гостевого дашборда не
    // загружались вовсе. Перевод делается здесь, а не на бэкенде:
    // придумывать серверу второе имя для той же колонки значило бы держать
    // два контракта вместо одного.
    visibility: filters?.isPublic === true ? EventVisibility.PUBLIC : undefined,
    tags: filters?.tags,
    // AD-388. Место, цена и порядок — часть контракта списка: каталог
    // предлагал их человеку, а отбирал по уже загруженной странице.
    location: filters?.location,
    minPrice: filters?.minPrice,
    maxPrice: filters?.maxPrice,
    sortBy: filters?.sortBy,
    sortOrder: filters?.sortOrder,
  };
};

// ===== API МЕТОДЫ =====

export const eventsApi = {
  // ===== УПРАВЛЕНИЕ СОБЫТИЯМИ =====

  // Получение списка событий с фильтрацией
  // ADR-002: data = Event[], пагинация (total/page/limit) — в meta
  getEvents: async (filters?: EventFilters): Promise<ApiResponse<Event[]>> => {
    const limit = filters?.limit ?? 20;
    const page = filters?.page ?? 1;

    return httpClient.get(
      `/events${buildQueryString({
        skip: (page - 1) * limit,
        take: limit,
        ...eventsSelectionQuery(filters),
      })}`,
    );
  },

  /**
   * AD-388. Справочник категорий каталога — значения, которые ДЕЙСТВИТЕЛЬНО
   * стоят у событий (`GET /events/categories`). Не путать с
   * `getEventCategories()`: тот отдаёт статический справочник ТИПОВ события
   * (`/event-types/categories`) и к полю `Event.categories` отношения не имеет.
   */
  getCatalogCategories: async (): Promise<ApiResponse<string[]>> => {
    return httpClient.get("/events/categories");
  },

  /**
   * Auto-saves the wizard draft via the minimal /events/draft endpoint.
   * Unlike createEvent, description is optional — the wizard fills it progressively.
   */
  createDraftEvent: async (
    data: Pick<
      CreateEventRequest,
      | "title"
      | "description"
      | "type"
      | "startDate"
      | "endDate"
      | "timezone"
      | "tags"
      | "maxParticipants"
    >,
  ): Promise<ApiResponse<Event>> => {
    const payload = {
      title: data.title,
      ...(data.description ? { description: data.description } : {}),
      type: data.type,
      startDate:
        data.startDate instanceof Date
          ? data.startDate.toISOString()
          : data.startDate,
      endDate:
        data.endDate instanceof Date
          ? data.endDate.toISOString()
          : data.endDate,
      // AD-527: пояс идёт вместе с датами — по нему сервер считает дни события.
      ...(data.timezone ? { timezone: data.timezone } : {}),
      tags: data.tags ?? [],
      ...(data.maxParticipants != null
        ? { maxParticipants: data.maxParticipants }
        : {}),
    };
    return httpClient.post("/events/draft", payload);
  },

  // Создание нового события (Backend: location — string, images — EventImageDto[])
  createEvent: async (
    data: CreateEventRequest,
  ): Promise<ApiResponse<Event>> => {
    const normalized: CreateEventRequest = {
      ...data,
      description: data.description ?? "",
      status: data.status ?? "draft",
      visibility: data.visibility ?? "private",
      startDate:
        data.startDate instanceof Date
          ? data.startDate.toISOString()
          : data.startDate,
      endDate:
        data.endDate instanceof Date
          ? data.endDate.toISOString()
          : data.endDate,
    };
    const payload = buildEventPayload(normalized, "create");
    return httpClient.post("/events", payload);
  },

  /** Вариант Б: сохранить бюджет мероприятия отдельным запросом после создания события (POST /events/:id/budget/items по одной статье). */
  createEventBudget: async (
    eventId: string,
    budget: Partial<EventBudget>,
  ): Promise<ApiResponse<unknown>> => {
    const items = budget.items ?? [];
    for (const item of items) {
      await httpClient.post(
        `/events/${eventId}/budget/items`,
        eventBudgetItemToCreateDto(item),
      );
    }
    return { success: true, data: null };
  },

  /** Получить статьи бюджета мероприятия (GET /events/:eventId/budget/items). */
  getEventBudgetItems: async (
    eventId: string,
  ): Promise<ApiResponse<EventBudgetItem[]>> => {
    const response = await httpClient.get(`/events/${eventId}/budget/items`);
    if (Array.isArray(response.data))
      return response as ApiResponse<EventBudgetItem[]>;
    const data = (response.data as { items?: EventBudgetItem[] })?.items ?? [];
    return { success: true, data };
  },

  /** Получить сводку бюджета мероприятия (GET /events/:eventId/budget/summary). */
  getEventBudgetSummary: async (
    eventId: string,
  ): Promise<
    ApiResponse<{
      totalPlanned: number;
      totalActual: number;
      totalPaid: number;
      totalIncome: number;
      totalExpenses: number;
      profit: number;
      variance: number;
      totalItems: number;
      currency?: string;
    }>
  > => {
    return httpClient.get(`/events/${eventId}/budget/summary`);
  },

  /** Создать статью бюджета мероприятия (POST /events/:eventId/budget/items). */
  createEventBudgetItem: async (
    eventId: string,
    item: {
      title: string;
      description?: string;
      type: string;
      category: string;
      status?: string;
      plannedAmount: number;
      actualAmount?: number;
      paidAmount?: number;
      dueDate?: Date;
      paymentDate?: Date;
      vendor?: string;
      invoiceNumber?: string;
      receiptUrl?: string;
      isActive?: boolean;
    },
  ): Promise<ApiResponse<EventBudgetItem>> => {
    return httpClient.post(`/events/${eventId}/budget/items`, item);
  },

  /** Обновить статью бюджета мероприятия (PUT /events/:eventId/budget/items/:itemId). */
  updateEventBudgetItem: async (
    eventId: string,
    itemId: string,
    item: Partial<{
      title: string;
      description: string;
      type: string;
      category: string;
      status: string;
      plannedAmount: number;
      actualAmount: number;
      paidAmount: number;
      dueDate: Date;
      paymentDate: Date;
      vendor: string;
      invoiceNumber: string;
      receiptUrl: string;
      isActive: boolean;
    }>,
  ): Promise<ApiResponse<EventBudgetItem>> => {
    return httpClient.put(`/events/${eventId}/budget/items/${itemId}`, item);
  },

  /** Удалить статью бюджета мероприятия (DELETE /events/:eventId/budget/items/:itemId). */
  deleteEventBudgetItem: async (
    eventId: string,
    itemId: string,
  ): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/events/${eventId}/budget/items/${itemId}`);
  },

  /** Обновить статус статьи бюджета (PUT /events/:eventId/budget/items/:itemId/status). */
  updateBudgetItemStatus: async (
    eventId: string,
    itemId: string,
    status: string,
  ): Promise<ApiResponse<EventBudgetItem>> => {
    return httpClient.put(`/events/${eventId}/budget/items/${itemId}/status`, {
      status,
    });
  },

  /** Записать оплату по статье бюджета (POST /events/:eventId/budget/items/:itemId/payment). */
  recordBudgetPayment: async (
    eventId: string,
    itemId: string,
    amount: number,
    paymentDate?: string,
  ): Promise<ApiResponse<EventBudgetItem>> => {
    return httpClient.post(
      `/events/${eventId}/budget/items/${itemId}/payment`,
      {
        amount,
        paymentDate: paymentDate ?? new Date().toISOString(),
      },
    );
  },

  /** Скачать финансовый отчёт мероприятия (GET /events/:eventId/budget/reports). Возвращает Blob файла. */
  getBudgetReports: async (eventId: string): Promise<Blob> => {
    const response = await httpClient.get(`/events/${eventId}/budget/reports`, {
      responseType: "blob",
    });
    return response.data as Blob;
  },

  /** Получить просроченные статьи бюджета (GET /events/:eventId/budget/overdue). */
  getOverdueBudgetItems: async (
    eventId: string,
  ): Promise<ApiResponse<EventBudgetItem[]>> => {
    return httpClient.get(`/events/${eventId}/budget/overdue`);
  },

  // ===== УПРАВЛЕНИЕ АГЕНДОЙ =====

  getAgendaItems: async (eventId: string) =>
    httpClient.get(`/events/${eventId}/agenda`),

  createAgendaItem: async (
    eventId: string,
    dto: {
      title: string;
      description?: string;
      type: string;
      startTime: string;
      endTime: string;
      location?: string;
      room?: string;
      maxParticipants?: number;
      order?: number;
    },
  ) => httpClient.post(`/events/${eventId}/agenda`, dto),

  updateAgendaItem: async (
    eventId: string,
    itemId: string,
    dto: Partial<{
      title: string;
      description: string;
      type: string;
      startTime: string;
      endTime: string;
      location: string;
      room: string;
      maxParticipants: number;
      order: number;
      isActive: boolean;
    }>,
  ) => httpClient.put(`/events/${eventId}/agenda/${itemId}`, dto),

  deleteAgendaItem: async (eventId: string, itemId: string) =>
    httpClient.delete(`/events/${eventId}/agenda/${itemId}`),

  reorderAgendaItems: async (eventId: string, itemIds: string[]) =>
    httpClient.put(`/events/${eventId}/agenda/reorder`, { itemIds }),

  getAgendaStatistics: async (eventId: string) =>
    httpClient.get(`/events/${eventId}/agenda/statistics`),

  checkAgendaConflicts: async (eventId: string) =>
    httpClient.get(`/events/${eventId}/agenda/conflicts`),

  // AD-529: накладки в НЕсохранённой программе (мастер) — то же правило сервера.
  checkProposedAgendaConflicts: async (items: ProposedAgendaItem[]) =>
    httpClient.post("/agenda-conflicts/check", { items }),

  addAgendaSpeaker: async (
    eventId: string,
    itemId: string,
    dto: {
      userId?: string;
      name: string;
      bio?: string;
      avatarUrl?: string;
      topic?: string;
    },
  ) => httpClient.post(`/events/${eventId}/agenda/${itemId}/speakers`, dto),

  removeAgendaSpeaker: async (
    eventId: string,
    itemId: string,
    speakerId: string,
  ) =>
    httpClient.delete(
      `/events/${eventId}/agenda/${itemId}/speakers/${speakerId}`,
    ),

  addAgendaMaterial: async (
    eventId: string,
    itemId: string,
    material: {
      title: string;
      url: string;
      type: "document" | "presentation" | "video" | "audio" | "image";
      description?: string;
    },
  ) =>
    httpClient.post(`/events/${eventId}/agenda/${itemId}/materials`, material),

  removeAgendaMaterial: async (
    eventId: string,
    itemId: string,
    materialId: string,
  ) =>
    httpClient.delete(
      `/events/${eventId}/agenda/${itemId}/materials/${materialId}`,
    ),

  // ===== ГАЛЕРЕЯ ИЗОБРАЖЕНИЙ =====

  /**
   * Загрузить фотографии мероприятия (POST /events/:id/images/upload).
   * Ответ приходит до антивирусной проверки: `images` пуст, а `files` несёт
   * квитанции; итог по файлу — `filesApi.getFileStatus` (AD-665, AD-688).
   */
  uploadEventImages: async (
    eventId: string,
    files: File[],
    isCover?: boolean,
  ): Promise<ApiResponse<EventImagesUploadEventImagesResponses[201]>> => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    if (isCover !== undefined) formData.append("isCover", String(isCover));
    return httpClient.post(`/events/${eventId}/images/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  /**
   * Получить галерею изображений (GET /events/:id/images).
   *
   * В `data` конверта ADR-002 лежит СТРАНИЦА галереи, а не массив: прежняя
   * сигнатура обещала массив, и экран, проверявший `Array.isArray`, показывал
   * пустую галерею при любом числе загруженных изображений.
   */
  getEventImages: async (
    eventId: string,
  ): Promise<
    ApiResponse<{
      images: Array<{
        /**
         * Устойчивое опознание изображения. Именно его, а не позицию в
         * массиве, надо слать в delete/set-cover: пока экран держит
         * загруженный список, соседняя правка сдвигает массив, и «удалить
         * третье» доезжает до БД как «удалить то, что стало третьим» (Н-15).
         * Необязательное только ради изображений, загруженных до этой правки.
         */
        id?: string;
        url: string;
        alt?: string;
        isCover?: boolean;
        source?: string;
      }>;
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>
  > => {
    return httpClient.get(`/events/${eventId}/images`);
  },

  /** Добавить изображения из общей галереи (POST /events/:id/images/from-gallery) */
  addImagesFromGallery: async (
    eventId: string,
    imageUrls: string[],
  ): Promise<ApiResponse<{ images: unknown[] }>> => {
    return httpClient.post(`/events/${eventId}/images/from-gallery`, {
      imageUrls,
    });
  },

  /**
   * Удалить изображение (DELETE /events/:id/images/:imageRef).
   *
   * `imageRef` — `id` изображения из галереи. Число (позиция) бэкенд ещё
   * принимает ради собранных ранее мобильных клиентов, но эта форма адресует
   * не изображение, а место в массиве: соседняя правка сдвигает список, и
   * удаляется чужая запись вместе с её файлом (Н-15). Числовую форму слать
   * только тогда, когда у записи вообще нет `id`.
   *
   * 404 EVENT_IMAGE_NOT_FOUND — изображение уже удалено кем-то другим;
   * это повод перечитать галерею, а не показывать общий сбой.
   */
  deleteEventImage: async (
    eventId: string,
    imageRef: string | number,
  ): Promise<ApiResponse<void>> => {
    const ref = encodeURIComponent(String(imageRef));
    return httpClient.delete(`/events/${eventId}/images/${ref}`);
  },

  /** Установить изображение как обложку (POST /events/:id/images/:imageRef/set-cover) */
  setEventImageAsCover: async (
    eventId: string,
    imageRef: string | number,
  ): Promise<ApiResponse<void>> => {
    const ref = encodeURIComponent(String(imageRef));
    return httpClient.post(`/events/${eventId}/images/${ref}/set-cover`);
  },

  /** AI-рекомендации изображений (POST /events/:id/images/ai-recommendations) */
  getAiImageRecommendations: async (
    eventId: string,
  ): Promise<
    ApiResponse<{
      recommendations: Array<{ url: string; description: string }>;
    }>
  > => {
    return httpClient.post(`/events/${eventId}/images/ai-recommendations`);
  },

  /** Категории изображений (GET /events/image-categories) */
  getImageCategories: async (): Promise<
    ApiResponse<Array<{ id: string; name: string }>>
  > => {
    return httpClient.get("/events/image-categories");
  },

  // ===== ПОСЕЩАЕМОСТЬ =====

  /**
   * F2.1 (3.2): QR-чек-ин участника ПО КОДУ — серверный резолв скана
   * (POST /events/:eventId/checkin/by-code). `code` = registrationCode из QR
   * (fallback — participantId). Клиенту НЕ нужен полный список участников:
   * сервер сам резолвит скан (корректно для mega-событий, без риска «страницы 2+»).
   * Идемпотентно: повторный скан → alreadyCheckedIn=true без двойного эффекта.
   *
   * «Кто отметил» сервер берёт из токена (21.09.2026): поле `enteredBy` из тела
   * снято, и прислать его теперь значит получить 400.
   */
  checkInByCode: async (
    eventId: string,
    code: string,
  ): Promise<ApiResponse<CheckInByCodeResult>> => {
    return httpClient.post(`/events/${eventId}/checkin/by-code`, { code });
  },

  /** Отметить участника как присутствующего */
  markAttendancePresent: async (
    eventId: string,
    userId: string,
  ): Promise<ApiResponse<void>> =>
    httpClient.post(
      `/events/attendance/event/${eventId}/user/${userId}/present`,
    ),

  /** Отметить участника как отсутствующего */
  markAttendanceAbsent: async (
    eventId: string,
    userId: string,
  ): Promise<ApiResponse<void>> =>
    httpClient.post(
      `/events/attendance/event/${eventId}/user/${userId}/absent`,
    ),

  /** Отметить участника как опоздавшего */
  markAttendanceLate: async (
    eventId: string,
    userId: string,
  ): Promise<ApiResponse<void>> =>
    httpClient.post(`/events/attendance/event/${eventId}/user/${userId}/late`),

  /** Зафиксировать выход участника */
  recordAttendanceCheckout: async (
    eventId: string,
    userId: string,
  ): Promise<ApiResponse<void>> =>
    httpClient.post(
      `/events/attendance/event/${eventId}/user/${userId}/checkout`,
    ),

  /**
   * Сводка посещаемости мероприятия — ЗЕРКАЛО серверного
   * `EventAttendanceSummary` (AD-464).
   *
   * Поля названы так, как их отдаёт сервер. Прежний тип объявлял `present`,
   * которого в ответе нет (плитка «Присутствуют» пустовала), и умалчивал
   * масштаб доли — экран умножал уже-проценты на 100 и печатал «Явка 3333%».
   */
  getAttendanceSummary: async (
    eventId: string,
  ): Promise<ApiResponse<AttendanceSummaryResponse>> =>
    httpClient.get(`/events/attendance/event/${eventId}/summary`),

  /**
   * Журнал отметок события (`GET /events/attendance?eventId=…`).
   *
   * Это ЖУРНАЛ, а не список участников: строка появляется только от отметки,
   * и человека, которого никто не отмечал, здесь нет вовсе. Круг участников
   * даёт `getEventParticipants`.
   *
   * Поля — те, что реально приходят с провода (сущность `EventAttendance`).
   * Прежнее объявление обещало плоские `userName`/`userEmail`: сервер кладёт
   * пользователя ВЛОЖЕННО (`user`), и обе ячейки экрана всегда показывали
   * фолбэк — UUID вместо имени и прочерк вместо почты.
   */
  getAttendanceRecords: async (
    eventId: string,
  ): Promise<ApiResponse<AttendanceJournalRow[]>> =>
    httpClient.get("/events/attendance", { params: { eventId } }),

  // Получение события по ID
  getEvent: async (id: string): Promise<ApiResponse<Event>> => {
    return httpClient.get(`/events/${id}`);
  },

  // Живое событие (live)
  getLiveEvent: async (id: string): Promise<ApiResponse<Event>> => {
    return httpClient.get(`/events/${id}/live`);
  },

  // Обновление события (Backend: PATCH; location — string, images — EventImageDto[])
  updateEvent: async (
    id: string,
    data: UpdateEventRequest,
  ): Promise<ApiResponse<Event>> => {
    const payload = buildEventPayload(data as CreateEventRequest, "update");
    return httpClient.patch(`/events/${id}`, payload);
  },

  // Удаление события
  deleteEvent: async (
    id: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/events/${id}`);
  },

  // Изменение статуса события
  updateEventStatus: async (
    id: string,
    status: EventStatus,
  ): Promise<ApiResponse<Event>> => {
    return httpClient.patch(`/events/${id}/status`, { status });
  },

  // Публикация события
  publishEvent: async (id: string): Promise<ApiResponse<Event>> => {
    return httpClient.post(`/events/${id}/publish`);
  },

  // Отмена публикации события
  unpublishEvent: async (id: string): Promise<ApiResponse<Event>> => {
    return httpClient.post(`/events/${id}/unpublish`);
  },

  /**
   * Отмена САМОГО мероприятия (не публикации): статус → `cancelled`,
   * записавшиеся получают уведомление (`event.cancelled` → слушатель
   * уведомлений). Маршрут на сервере существовал с самого начала и лежал в
   * генерате (`EventsCancelData`), но не вызывался НИ ОДНИМ экраном — при этом
   * отказ удаления (`EVENT_HAS_REGISTERED_PARTICIPANTS`) велел «отмените
   * мероприятие вместо удаления». Найдено боевым прогоном Владимира 18.09.2026.
   *
   * Тела у запроса нет: причину отмены сервер не принимает.
   */
  cancelEvent: async (id: string): Promise<ApiResponse<Event>> => {
    return httpClient.post(`/events/${id}/cancel`);
  },

  /**
   * AD-465 (решение Сергея 25.09.2026: кнопка и автозавершение). Завершение
   * мероприятия кнопкой: статус → `completed`. Маршрут на сервере был всегда,
   * но ни один экран его не звал, и событие, не завершённое руками, оставалось
   * «опубликованным» навсегда. На повтор нажатия и на нажатие после
   * автозавершения сервер отвечает завершённым событием как есть, без второй
   * рассылки.
   *
   * Тела у запроса нет.
   */
  completeEvent: async (id: string): Promise<ApiResponse<Event>> => {
    return httpClient.post(`/events/${id}/complete`);
  },

  // Дублирование события
  duplicateEvent: async (
    id: string,
    newTitle?: string,
  ): Promise<ApiResponse<Event>> => {
    return httpClient.post(`/events/${id}/duplicate`, { newTitle });
  },

  // ===== УЧАСТНИКИ СОБЫТИЙ =====

  // Получение участников события
  getEventParticipants: async (
    eventId: string,
    filters?: ParticipantFilters,
  ): Promise<ParticipantsPageResponse> => {
    return httpClient.get(
      `/events/${eventId}/participants${buildQueryString(filters || {})}`,
    );
  },

  /**
   * Лист ожидания события — ОТДЕЛЬНЫЙ эндпоинт, а не фильтр по таблице участников.
   *
   * `getEventParticipants({ status: 'waitlisted' })` для этого не годится: тот
   * маршрут сортирует `registeredAt DESC` (свежие сверху — верно для таблицы), а
   * очередь идёт в обратном порядке. На его выдаче первая страница содержала бы
   * ХВОСТ очереди, а начала очереди при длине больше страницы не было бы видно
   * вовсе. Здесь порядок — тот же, которым бэкенд повышает, и позиции считает он же.
   */
  getEventWaitlist: async (
    eventId: string,
    pagination?: { page?: number; limit?: number },
  ): Promise<WaitlistPageResponse> => {
    return httpClient.get(
      `/events/${eventId}/waitlist${buildQueryString(pagination || {})}`,
    );
  },

  /**
   * AD-603. Выключатель уведомлений о действиях гостей по событию — у САМОГО
   * запрашивающего управляющего (организатор, соорганизатор, администратор
   * события), на других он не действует. Сегодня под ним — уведомления в
   * кабинет об ответах гостей на приглашение.
   *
   * PUT отключает, DELETE включает обратно; повтор любого из них безвреден, оба
   * возвращают положение после записи. Персоналу платформы без роли в событии
   * сервер отвечает 403: этих уведомлений у него нет, выключать нечего.
   */
  getEventNotificationMute: async (
    eventId: string,
  ): Promise<ApiResponse<EventNotificationMuteStateDto>> => {
    return httpClient.get(`/events/${eventId}/notification-mute`);
  },

  muteEventNotifications: async (
    eventId: string,
  ): Promise<ApiResponse<EventNotificationMuteStateDto>> => {
    return httpClient.put(`/events/${eventId}/notification-mute`);
  },

  unmuteEventNotifications: async (
    eventId: string,
  ): Promise<ApiResponse<EventNotificationMuteStateDto>> => {
    return httpClient.delete(`/events/${eventId}/notification-mute`);
  },

  // Добавление участника на BE происходит ТОЛЬКО через приглашение
  // (inviteParticipants → POST /events/:id/invite). Прямого POST
  // /events/:id/participants на BE нет — метод addEventParticipant удалён (A10),
  // все потребители переведены на inviteParticipants.

  // Приглашение участников (ADR-INVITES-post-creation-invite-delivery):
  // userIds — зарегистрированные; emails — любые (зарегистрированным создаётся
  // запись INVITED + уведомление, незарегистрированным уходит письмо-приглашение).
  // ВАЖНО: BE валидирует @IsEmail(each) — один невалидный адрес отклоняет весь
  // запрос, поэтому FE обязан валидировать адреса ДО отправки.
  inviteParticipants: async (
    eventId: string,
    data: InviteParticipantsRequest,
  ): Promise<ApiResponse<InviteParticipantsResult>> => {
    return httpClient.post(`/events/${eventId}/invite`, data);
  },

  // RSVP-отклик приглашённого (ADR-RSVP-invitation-response-loop).
  // going → участник REGISTERED/WAITLISTED, not_going → CANCELED, maybe → INVITED.
  //
  // AD-414. `participationMode` едет ТОЛЬКО с `going` и только у гибрида: ответ
  // «приду» — это и есть участие, а у «не приду» и «возможно» способа участия
  // нет, и сервер отвергает такое тело кодом `PARTICIPATION_MODE_REQUIRES_GOING`
  // (у негибрида — `PARTICIPATION_MODE_NOT_APPLICABLE`). Поле кладётся в тело,
  // только когда способ назван: пустое поле рядом с `response` означало бы
  // «заявляю, что не заявляю».
  respondToInvitation: async (
    eventId: string,
    response: "going" | "not_going" | "maybe",
    participationMode?: ParticipationMode | null,
  ): Promise<ApiResponse<RsvpResult>> => {
    return httpClient.post(`/events/${eventId}/rsvp`, {
      response,
      ...(participationMode ? { participationMode } : {}),
    });
  },

  // Удаление участника (управленческое: организатор/админ).
  // BE: DELETE /events/:eventId/participants/:userId (204). Ключ — userId
  // участника (НЕ participant.id): ровно этим и был баг A10 (несовпадение ключей).
  removeEventParticipant: async (
    eventId: string,
    userId: string,
  ): Promise<ApiResponse<void>> => {
    return httpClient.delete(`/events/${eventId}/participants/${userId}`);
  },

  // Изменение статуса участника.
  // BE: PATCH /events/:eventId/participants/:userId с телом { status }
  // (UpdateParticipantDto). Отдельного .../:userId/status на BE нет.
  updateParticipantStatus: async (
    eventId: string,
    userId: string,
    status: ParticipantStatus,
  ): Promise<ApiResponse<EventParticipant>> => {
    return httpClient.patch(`/events/${eventId}/participants/${userId}`, {
      status,
    });
  },

  // Массовое обновление статусов участников.
  // BE: PATCH /events/:eventId/participants/bulk-status, тело
  // { userIds, status } (BulkParticipantStatusDto, whitelist+forbidNonWhitelisted
  // — ключ ДОЛЖЕН быть userIds, не participantIds).
  bulkUpdateParticipantStatuses: async (
    eventId: string,
    userIds: string[],
    status: ParticipantStatus,
  ): Promise<ApiResponse<{ updated: number }>> => {
    return httpClient.patch(`/events/${eventId}/participants/bulk-status`, {
      userIds,
      status,
    });
  },

  // Массовое удаление участников.
  // BE: DELETE /events/:eventId/participants/bulk, тело { userIds }
  // (BulkParticipantIdsDto). DELETE с телом — через axios config.data.
  bulkRemoveParticipants: async (
    eventId: string,
    userIds: string[],
  ): Promise<ApiResponse<{ removed: number }>> => {
    return httpClient.delete(`/events/${eventId}/participants/bulk`, {
      data: { userIds },
    });
  },

  // ===== РЕГИСТРАЦИЯ НА СОБЫТИЯ =====

  /**
   * Запись на событие (`POST /events/:id/register`).
   *
   * AD-389 шаг 6. Тело сужено до способа участия — и это не косметика типа.
   * Прежде оно было объявлено как `Omit<CreateParticipantRequest, "eventId">`
   * (питание, пожелания, экстренный контакт), а маршрут тела не имел ВОВСЕ и
   * молча всё выбрасывал. Теперь тело у маршрута есть, проверяется классом и
   * отвергает чужие поля (`forbidNonWhitelisted`): отправь экран прежний набор
   * — получил бы 400 на записи. Ни один из вызывающих сегодня тела не шлёт,
   * так что сужение ничего не ломает, а врущий тип убирает.
   *
   * Способ принимается ТОЛЬКО у гибрида: у очного и онлайнового он уже выражен
   * форматом, и присланное значение сервер отвергнет кодом
   * `PARTICIPATION_MODE_NOT_APPLICABLE`.
   */
  registerForEvent: async (
    eventId: string,
    data?: { participationMode?: ParticipationMode },
  ): Promise<ApiResponse<RegisterForEventResult>> => {
    return httpClient.post(`/events/${eventId}/register`, data || {});
  },

  /**
   * AD-389 шаг 6. Смена собственного заявленного способа участия
   * (`PATCH /events/:id/participation-mode`).
   *
   * Без этого маршрута записавшийся заперт в своём ответе, а планы меняются до
   * самого события — и чем ближе оно, тем важнее организатору точное число.
   * Маршрут самоскоуплен по токену: чьё намерение менять, сервер берёт оттуда,
   * параметра «за кого» нет и здесь быть не должно.
   *
   * Отозвать ответ обратно в «не заявлено» нельзя намеренно: «я передумал и не
   * скажу» не состояние, полезное для планирования, а `null` описывает тех,
   * кого не спрашивали.
   */
  setParticipationMode: async (
    eventId: string,
    participationMode: ParticipationMode,
  ): Promise<ApiResponse<ParticipationModeResult>> => {
    return httpClient.patch(`/events/${eventId}/participation-mode`, {
      participationMode,
    });
  },

  // Отмена регистрации (Backend: DELETE)
  unregisterFromEvent: async (
    eventId: string,
  ): Promise<ApiResponse<UnregisterFromEventResult>> => {
    return httpClient.delete(`/events/${eventId}/unregister`);
  },

  /** Одобрить участника (POST /events/:id/approve/:userId) */
  approveParticipant: async (
    eventId: string,
    userId: string,
    currentUserId: string,
  ): Promise<ApiResponse<EventParticipant>> => {
    return httpClient.post(`/events/${eventId}/approve/${userId}`, {
      requestedBy: currentUserId,
    });
  },

  /** Отклонить участника (POST /events/:id/reject/:userId) */
  rejectParticipant: async (
    eventId: string,
    userId: string,
    currentUserId: string,
  ): Promise<ApiResponse<EventParticipant>> => {
    return httpClient.post(`/events/${eventId}/reject/${userId}`, {
      requestedBy: currentUserId,
    });
  },

  /** Повысить участника из листа ожидания (POST /events/:id/promote/:userId) */
  promoteFromWaitlist: async (
    eventId: string,
    userId: string,
  ): Promise<
    ApiResponse<{ success: boolean; message: string; status: string }>
  > => {
    return httpClient.post(`/events/${eventId}/promote/${userId}`, {});
  },

  /**
   * Получить QR-код участника (GET /events/:id/qr/:userId).
   *
   * AD-460. Право проверяет СЕРВЕР и по своему правилу «свой QR или организатор»
   * (`ParticipantAttendanceService.getParticipantQR`): кто спрашивает, он берёт из
   * токена, а `requestedBy` в строке запроса не читает вовсе. Параметр оставлен
   * прежним, чтобы не переписывать зовущие места, но решать по нему нельзя —
   * подстановка чужого идентификатора ничего не откроет и ничего не закроет.
   */
  getParticipantQR: async (
    eventId: string,
    userId: string,
    currentUserId: string,
  ): Promise<ApiResponse<ParticipantQRResponse>> => {
    return httpClient.get(`/events/${eventId}/qr/${userId}`, {
      params: { requestedBy: currentUserId },
    });
  },

  // Статус регистрации
  getRegistrationStatus: async (
    eventId: string,
  ): Promise<ApiResponse<OwnRegistrationStatus>> => {
    return httpClient.get(`/events/${eventId}/registration-status`);
  },

  /**
   * AD-389 шаг 4. Вход на онлайн-часть события — отдельным маршрутом, а не
   * полем карточки: карточку читает кто угодно, кому событие видно, а ссылка
   * на встречу — значение на предъявителя (тот же довод, что у токена
   * публичной регистрации, AD-299).
   *
   * Маршрут самоскоуплен по JWT: чьё участие спрашивать, сервер берёт из
   * токена, параметра «за кого» нет.
   */
  getOnlineAccess: async (
    eventId: string,
  ): Promise<ApiResponse<EventOnlineAccess>> => {
    return httpClient.get(`/events/${eventId}/online-access`);
  },

  /**
   * AD-389 шаг 4. Переход на онлайн-встречу — он же отметка присутствия
   * (решение владельца: «чек-ин на онлайн-мероприятии — по факту перехода по
   * ссылке»).
   *
   * ПОЧЕМУ ОТДЕЛЬНОЕ ДЕЙСТВИЕ, А НЕ ТОТ ЖЕ `getOnlineAccess`. Чтение входа
   * делают и карточка события, и мастер правки организатора; отмечать на нём
   * значило бы записывать присутствующим всякого, кто открыл страницу.
   *
   * Повтор безопасен: сервер отвечает 200 и `attendanceRecorded: false`, вход
   * выдаёт как обычно — со второго устройства человек тоже должен попасть на
   * встречу.
   */
  joinOnlineEvent: async (
    eventId: string,
  ): Promise<ApiResponse<EventOnlineJoin>> => {
    return httpClient.post(`/events/${eventId}/online-access/join`, {});
  },

  /**
   * AD-409. Что назначено событию, глазами управляющей стороны
   * (`GET /events/:id/online-access/link`).
   *
   * ПОЧЕМУ НЕ `getOnlineAccess`. Тот маршрут отвечает на вопрос участника
   * («пустят ли меня»), и после гейта формата у очного события он не отдаёт
   * ссылку НИКОМУ, включая организатора, — правильно для входа и бесполезно для
   * правки: снять оставшуюся ссылку нельзя, не увидев её.
   *
   * Формат события здесь не спрашивается — обязательное условие AD-409.
   */
  getManagedOnlineLink: async (
    eventId: string,
  ): Promise<ApiResponse<ManagedOnlineLink>> => {
    return httpClient.get(`/events/${eventId}/online-access/link`);
  },

  /**
   * AD-409. Снять назначенную ссылку на встречу
   * (`DELETE /events/:id/online-access/link`).
   *
   * Явный жест, отличимый от «не трогать»: пустое поле мастера по-прежнему
   * значит «оставить как было» (AD-389 шаг 4, `buildEventPayload`), и отменять
   * это правило нельзя — иначе правка описания стирала бы адрес встречи у всех
   * участников. Снятие получило собственное действие, а не второе прочтение
   * пустой строки.
   *
   * Идемпотентно: снятие уже снятой ссылки отвечает 200 и тем же состоянием.
   */
  clearOnlineLink: async (
    eventId: string,
  ): Promise<ApiResponse<ManagedOnlineLink>> => {
    return httpClient.delete(`/events/${eventId}/online-access/link`);
  },

  /**
   * AD-409. Снять назначенный адрес трансляции
   * (`DELETE /events/:id/online-access/stream`). Отдельный жест: снимать
   * трансляцию заодно со ссылкой значило бы стирать данные, которых никто не
   * просил стирать.
   */
  clearOnlineStream: async (
    eventId: string,
  ): Promise<ApiResponse<ManagedOnlineLink>> => {
    return httpClient.delete(`/events/${eventId}/online-access/stream`);
  },

  /**
   * AD-389 шаг 4. Подтверждение СВОЕГО участия (`registered → confirmed`) —
   * единственный шаг к ссылке, который зависит от самого человека. До этого
   * маршрута статус мог сменить только организатор, и просьба «подтвердите
   * участие» была невыполнима с экрана.
   */
  confirmOwnParticipation: async (
    eventId: string,
  ): Promise<ApiResponse<ConfirmParticipationResult>> => {
    return httpClient.post(`/events/${eventId}/participation/confirm`, {});
  },

  // Проверка доступности регистрации
  checkRegistrationAvailability: async (
    eventId: string,
  ): Promise<
    ApiResponse<{
      available: boolean;
      remainingSlots: number;
      registrationDeadline?: string;
      requiresApproval: boolean;
    }>
  > => {
    return httpClient.get(`/events/${eventId}/registration-availability`);
  },

  // ===== КАТЕГОРИИ И ТЕГИ =====

  // Категории событий (обновлено согласно рекомендациям бекенда)
  getEventCategories: async (): Promise<
    ApiResponse<
      Array<{
        id: string;
        name: string;
        description?: string;
        color?: string;
        icon?: string;
        count?: number;
      }>
    >
  > => {
    return httpClient.get("/event-types/categories");
  },

  // Теги событий (обновлено согласно рекомендациям бекенда)
  getEventTags: async (): Promise<
    ApiResponse<
      Array<{ id: string; name: string; color?: string; count?: number }>
    >
  > => {
    return httpClient.get("/event-types");
  },

  // Поиск событий
  searchEvents: async (
    query: string,
    filters?: EventFilters,
  ): Promise<ApiResponse<Event[]>> => {
    // Backend ожидает skip/take вместо page/limit
    const limit = filters?.limit ?? 20;
    const page = filters?.page ?? 1;
    const typeVal = filters?.type;
    const t =
      typeVal !== undefined
        ? Array.isArray(typeVal)
          ? typeVal[0]
          : typeVal
        : undefined;
    const formatVal = filters?.format;
    const f =
      formatVal !== undefined
        ? Array.isArray(formatVal)
          ? formatVal[0]
          : formatVal
        : undefined;

    return httpClient.get(
      `/events/search${buildQueryString({
        q: query,
        skip: (page - 1) * limit,
        take: limit,
        type: t,
        format: f,
        status: filters?.status,
        category: filters?.category,
      })}`,
    );
  },

  // ===== СТАТИСТИКА И АНАЛИТИКА =====

  // Статистика событий
  getEventStats: async (): Promise<ApiResponse<EventStats>> => {
    return httpClient.get("/events/stats");
  },

  // Аналитика событий
  getEventAnalytics: async (params?: {
    startDate?: string;
    endDate?: string;
    category?: string;
    organizerId?: string;
  }): Promise<ApiResponse<EventAnalytics>> => {
    return httpClient.get(`/events/analytics${buildQueryString(params || {})}`);
  },

  /**
   * Статистика конкретного события — `GET /events/:id/stats`.
   *
   * AD-435. Подпись ниже — зеркало боевого `EventStatisticsDto` (BE
   * `modules/events/interfaces/analytics.interface.ts`), который возвращает
   * `EventAnalyticsService.getEventStatistics`. Прежняя подпись объявляла
   * обязательными три поля, которых сервер не считает и никогда не присылал
   * (`averageEngagement`, `topEngagementMetrics`, `registeredParticipants`), и
   * экраны читали их как измерения: `?? 0` выглядел умолчанием, а был
   * единственной веткой — «Вовлечённость 0%» и «Зарегистрировано 0» печатались
   * всегда.
   *
   * Поля, помеченные `?`, сервер СЕГОДНЯ не шлёт ни разу; они оставлены
   * необязательными, потому что экраны под них уже нарисованы и вопрос «считать
   * ли вовлечённость» — продуктовый, а не фронтовый. Пока их нет,
   * `undefined` обязан доходить до экрана как «нет данных», а не превращаться
   * в ноль.
   */
  getSingleEventStats: async (
    eventId: string,
  ): Promise<
    ApiResponse<{
      eventId: string;
      eventName: string;
      /**
       * AD-438: занявшие место гости — без отменившихся, листа ожидания,
       * приглашённых и без строки самого организатора. До правки сервер считал
       * здесь ВСЕ строки `event_participants`, и число расходилось с тем,
       * которое показывает дверь регистрации.
       */
      totalParticipants: number;
      confirmedParticipants: number;
      attendedParticipants: number;
      /** Счёт по статусу `REGISTERED` — имя поля BE, а не «ожидающие». */
      pendingParticipants: number;
      attendanceRate: number;
      confirmationRate: number;
      maxParticipants: number;
      /**
       * AD-438. Поля НЕТ, когда мест не ограничивали (`maxParticipants = 0` —
       * значение по умолчанию у создания события). Ноль в этом случае читался
       * бы как «мест не осталось», поэтому сервер поле не шлёт вовсе, а экран
       * обязан различать «не применимо» и «посчитано, ноль».
       */
      availableSpots?: number;
      status: string;
      startDate: string;
      endDate: string;
      /** Ниже — то, чего сервер не присылает; см. комментарий выше. */
      averageEngagement?: number;
      revenue?: number;
      topEngagementMetrics?: Array<{ metric: string; value: number }>;
    }>
  > => {
    return httpClient.get(`/events/${eventId}/stats`);
  },

  // Предстоящие события
  getUpcomingEvents: async (
    filters?: EventFilters,
  ): Promise<ApiResponse<Event[]>> => {
    return httpClient.get(`/events/upcoming${buildQueryString(filters || {})}`);
  },

  // Прошедшие события
  getPastEvents: async (
    filters?: EventFilters,
  ): Promise<ApiResponse<Event[]>> => {
    return httpClient.get(`/events/past${buildQueryString(filters || {})}`);
  },

  // ===== ЭКСПОРТ И ИМПОРТ =====

  /**
   * Выгрузка каталога мероприятий — синхронная отдача файла.
   *
   * AD-392. Подпись объявляла ответ как `ApiResponse<{ downloadUrl: string }>`, а
   * `GET /events/export` отдаёт САМ ФАЙЛ (`res.send(buffer)` с
   * `Content-Disposition: attachment`) — ссылки в ответе не было никогда, и
   * кнопка «экспорт» на каталоге не скачивала ничего ни разу: человек
   * получал «не удалось получить ссылку». Дефект тот же, что AD-138/AD-163
   * у выгрузки участников, и чинится тем же приёмом: `getBlob` здесь,
   * запись файла — в хуке `useExportEvents`.
   *
   * Формат ФАЙЛА называется `fileFormat`: имя `format` у мероприятия занято
   * форматом участия (AD-389), и прежний `format: 'csv'` теперь получает отказ:
   * значение не из словаря `online | offline | hybrid`.
   */
  exportEvents: async (
    filters?: EventFilters,
    options?: { fileFormat?: EventExportFileFormat },
  ): Promise<BlobDownload> => {
    const fileFormat: EventExportFileFormat = options?.fileFormat ?? "csv";
    return httpClient.getBlob(
      `/events/export${buildQueryString({
        ...eventsSelectionQuery(filters),
        // Маршрут по построению отдаёт только СВОИ мероприятия, а чужого
        // организатора отклоняет отказом (BE AD-390): подставить вместо
        // него себя значило бы снова тихо проглотить параметр.
        organizerId: undefined,
        fileFormat,
      })}`,
      // Тип ответа проверяется намеренно: при редиректе на вход в файл
      // уезжает `index.html`, и человек получает «выгрузку», которую таблица не
      // открывает. Дискриминаторы те же, что у BE-mimeType.
      { expectedType: fileFormat === "xlsx" ? "spreadsheet" : "csv" },
    );
  },

  /**
   * Экспорт участников события — синхронная выгрузка файла.
   *
   * AD-138/AD-163. Прежняя подпись объявляла ответ как
   * `ApiResponse<{ downloadUrl: string }>`, а маршрут `GET
   * /events/:eventId/participants/export` отдаёт САМ ФАЙЛ (`res.send(buffer)` с
   * `Content-Disposition: attachment`). Ссылки в ответе не было никогда, поэтому
   * ни один экран файла не получал, а `httpClient.get` вдобавок возвращал
   * `response.data`, то есть первые байты файла в виде «конверта».
   *
   * Словарь форматов — бэкендовский (`ExportParticipantsQueryDto`): `csv | xlsx`.
   * Общий `ExportOptions` здесь НЕ используется: у него `excel` и `pdf`, которых
   * маршрут не знает, и с AD-138 расхождение перестало быть безобидным — лишнее
   * значение теперь отклоняется с 400, а не подменяется на `csv` молча.
   */
  exportEventParticipants: async (
    eventId: string,
    filters?: Pick<ParticipantFilters, "status" | "role">,
    options?: { format?: ParticipantExportFormat },
  ): Promise<BlobDownload> => {
    const format: ParticipantExportFormat = options?.format ?? "csv";
    return httpClient.getBlob(
      `/events/${eventId}/participants/export${buildQueryString({
        status: filters?.status,
        role: filters?.role,
        format,
      })}`,
      // Тип ответа проверяется намеренно: при неверном адресе или редиректе на
      // вход в файл уезжает `index.html`, и человек получает «выгрузку», которую
      // таблица не открывает. Дискриминаторы те же, что у BE-mimeType:
      // `text/csv` и `…spreadsheetml.sheet`.
      { expectedType: format === "xlsx" ? "spreadsheet" : "csv" },
    );
  },

  // Импорт участников
  importEventParticipants: async (
    eventId: string,
    file: File,
  ): Promise<
    ApiResponse<{
      imported: number;
      errors: Array<{ row: number; error: string }>;
    }>
  > => {
    const formData = new FormData();
    formData.append("file", file);

    return httpClient.post(`/events/${eventId}/participants/import`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // ===== УВЕДОМЛЕНИЯ =====

  // Реальный контракт (event-notification-campaign, ADR-EVENT-NOTIFICATIONS):
  // POST /event-admin/notifications?eventId= (CreateEventNotificationDto, роль
  // EVENT_ADMIN). Сервис createCampaign материализует кампанию и — при
  // sendImmediately/наступившем scheduledAt — делегирует фактическую доставку
  // NotificationsService (in-app-строка + gated email/push/sms). recipientIds
  // (если пусто — все участники), каналы и планирование учитываются реально.
  sendEventNotification: async (
    eventId: string,
    data: {
      title: string;
      content: string;
      channels: EventNotificationChannel[];
      type?: EventNotificationType;
      priority?: EventNotificationPriority;
      recipientIds?: string[];
      sendImmediately?: boolean;
      scheduledAt?: string;
    },
  ): Promise<ApiResponse<{ success: boolean; message: string }>> => {
    return httpClient.post(
      `/event-admin/notifications`,
      {
        type: data.type ?? "event_update",
        priority: data.priority ?? "normal",
        title: data.title,
        content: data.content,
        channels: data.channels,
        ...(data.recipientIds && data.recipientIds.length > 0
          ? { recipientIds: data.recipientIds }
          : {}),
        sendImmediately: data.sendImmediately ?? true,
        ...(data.scheduledAt ? { scheduledAt: data.scheduledAt } : {}),
      },
      { params: { eventId } },
    );
  },

  // ===== ДОКУМЕНТЫ И МАТЕРИАЛЫ =====

  // Загрузка документа события
  uploadEventDocument: async (
    eventId: string,
    file: File,
    metadata?: { name: string; description?: string; category?: string },
  ): Promise<ApiResponse<EventDocument>> => {
    const formData = new FormData();
    formData.append("file", file);
    if (metadata) {
      formData.append("metadata", JSON.stringify(metadata));
    }

    return httpClient.post(`/events/${eventId}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Получение документов события
  getEventDocuments: async (
    eventId: string,
  ): Promise<ApiResponse<EventDocument[]>> => {
    return httpClient.get(`/events/${eventId}/documents`);
  },

  // Удаление документа события
  deleteEventDocument: async (
    eventId: string,
    documentId: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/events/${eventId}/documents/${documentId}`);
  },

  // Регистрация на событие (join)
  joinEvent: async (eventId: string): Promise<ApiResponse<Event>> => {
    return httpClient.post(`/events/${eventId}/join`);
  },

  // Выход из события (leave)
  leaveEvent: async (eventId: string): Promise<ApiResponse<Event>> => {
    return httpClient.post(`/events/${eventId}/leave`);
  },

  // Загрузка изображения события
  uploadEventImage: async (
    eventId: string,
    file: File,
  ): Promise<ApiResponse<{ imageUrl: string }>> => {
    const formData = new FormData();
    formData.append("image", file);
    return httpClient.post(`/events/${eventId}/image`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // События текущего пользователя
  getMyEvents: async (): Promise<ApiResponse<Event[]>> => {
    return httpClient.get("/events/my");
  },

  // Популярные события (сортировка по просмотрам/участникам на бэкенде).
  // ВАЖНО: backend использует ValidationPipe с forbidNonWhitelisted,
  // поэтому в query можно передавать только поля, описанные в PaginationDto (skip/take).
  // ADR-002: data = Event[], пагинация (total/page/limit/totalPages) — в meta
  getPopular: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<Event[]>> => {
    const page = Math.max(1, Number(params?.page) || 1);
    const limit = Math.min(Math.max(1, Number(params?.limit) || 20), 100);
    const skip = (page - 1) * limit;
    const searchParams = new URLSearchParams();
    searchParams.append("skip", String(skip));
    searchParams.append("take", String(limit));
    return httpClient.get(`/events/popular?${searchParams.toString()}`);
  },
};

// Экспорт типов для обратной совместимости
export type {
  Event,
  EventParticipant,
  EventType,
  EventFormat,
  EventStatus,
  ParticipantStatus,
  ParticipantRole,
  EventLocation,
  EventAgenda,
  EventBudget,
  EventDocument,
  EventStats,
};

// Экспорт EventCategory для совместимости
export type EventCategory = string;

export default eventsApi;
